import PropTypes from 'prop-types';
import {
  fieldArrayFieldsPropTypes,
  fieldArrayMetaPropTypes,
  fieldInputPropTypes,
  fieldMetaPropTypes,
  formPropTypes,
} from 'redux-form';
import {
  ReservationCancellationType,
  ReservationStatus,
  VenueUserFeedbackOptionType,
} from '@classpass/protobuf-definitions/lib/web/models/partners/reservation';
import { ITEM_AVAILABILITY_STATUS_OPTIONS } from '../features/Items/Items.constants';
import { PAYOUT_STATUS } from '../features/Stats/Stats.constants';

export const intlMessageShape = {
  id: PropTypes.string.isRequired,
  defaultMessage: PropTypes.string.isRequired,
};
export const intlMessage = PropTypes.shape(intlMessageShape);
// The three forms makeValidators accepts for a HookForm `required` prop: on its own, with the
// message to show when it fails, or as react-hook-form's own rule object.
export const requiredRule = PropTypes.oneOfType([
  PropTypes.bool,
  intlMessage,
  PropTypes.shape({ value: PropTypes.bool, message: intlMessage }),
]);
// react-intl@3.0 is 100% typescript and doesn't offer the intlShape
// copied from https://github.com/formatjs/react-intl/blob/2.x/src/types.js
export const intlShape = PropTypes.shape({
  defaultFormats: PropTypes.shape({}),
  defaultLocale: PropTypes.string,
  formatDate: PropTypes.func.isRequired,
  formatMessage: PropTypes.func.isRequired,
  formatNumber: PropTypes.func.isRequired,
  formatPlural: PropTypes.func.isRequired,
  formats: PropTypes.shape({}),
  formatters: PropTypes.shape({}),
  formatTime: PropTypes.func.isRequired,
  locale: PropTypes.string,
  messages: PropTypes.shape({}),
  onError: PropTypes.func,
  timeZone: PropTypes.string,
});

export const historyPropTypes = {
  length: PropTypes.number,
  action: PropTypes.string,
  location: PropTypes.shape({}),
  createHref: PropTypes.func,
  push: PropTypes.func,
  replace: PropTypes.func,
  go: PropTypes.func,
  goBack: PropTypes.func,
  goForward: PropTypes.func,
  block: PropTypes.func,
  listen: PropTypes.func,
};

export const classListPropTypes = PropTypes.arrayOf(
  PropTypes.shape({
    classId: PropTypes.number.isRequired,
    className: PropTypes.string.isRequired,
  })
);

/*
 * React-Hook-Form PropTypes
 */
export const reactHookFormPropTypes = {
  control: PropTypes.shape({
    // The RHF control properties should never be accessed directly. See docs: https://react-hook-form.com/docs/useform/control
  }),
};

/*
 * Redux-Form PropTypes
 */
export const reduxFormPropTypes = {
  form: PropTypes.shape(formPropTypes).isRequired,
  field: {
    input: PropTypes.shape(fieldInputPropTypes).isRequired,
    meta: PropTypes.shape(fieldMetaPropTypes).isRequired,
  },
  fieldArray: {
    fields: PropTypes.shape(fieldArrayFieldsPropTypes).isRequired,
    meta: PropTypes.shape(fieldArrayMetaPropTypes).isRequired,
  },
  context: PropTypes.shape({
    dirty: PropTypes.bool.isRequired,
    fields: PropTypes.oneOfType([PropTypes.shape(fieldArrayFieldsPropTypes), PropTypes.object]).isRequired,
    handleReset: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    initialValues: PropTypes.shape({}),
    invalid: PropTypes.bool.isRequired,
    pristine: PropTypes.bool.isRequired,
    submitFailed: PropTypes.bool.isRequired,
    submitSucceeded: PropTypes.bool.isRequired,
    submitting: PropTypes.bool.isRequired,
    valid: PropTypes.bool.isRequired,
    values: PropTypes.shape({}),
  }).isRequired,
};

export const integratorPropTypes = {
  name: PropTypes.string,
  description: PropTypes.string,
  logoImageUrl: PropTypes.string,
  supportsAppointments: PropTypes.bool,
  supportsClasses: PropTypes.bool,
  supportsFoodAndBev: PropTypes.bool,
};

export const itemPropType = PropTypes.shape({
  id: PropTypes.number,
  availabilityRecurrence: PropTypes.arrayOf(
    PropTypes.shape({
      weekday: PropTypes.oneOf(['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']),
      startTime: PropTypes.string,
      endTime: PropTypes.string,
    })
  ),
  availabilityStatus: PropTypes.oneOf(ITEM_AVAILABILITY_STATUS_OPTIONS),
  classId: PropTypes.number,
  category: PropTypes.string,
  currentTimeBlocks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      description: PropTypes.string,
      endDatetime: PropTypes.string,
      startDatetime: PropTypes.string,
    })
  ),
  externalClassId: PropTypes.number,
  description: PropTypes.string,
  openApiId: PropTypes.number,
  name: PropTypes.string,
  payoutRate: PropTypes.number,
  resourceId: PropTypes.number,
});

export const venuePropTypes = {
  address: PropTypes.string,
  countryCode: PropTypes.string,
  countryCodeShort: PropTypes.string,
  currencyCode: PropTypes.string,
  enabled: PropTypes.bool,
  isManualStudio: PropTypes.bool,
  isReservationsStoppable: PropTypes.bool,
  isSmartspotEligible: PropTypes.bool,
  msaId: PropTypes.number,
  requireSigned: PropTypes.bool,
  timezone: PropTypes.string,
  venueAliasUrl: PropTypes.string,
  venueCreationDate: PropTypes.string,
  venueId: PropTypes.number,
  venueName: PropTypes.string,
  venueSource: PropTypes.string,
  venueSubtitle: PropTypes.string,
};

export const opportunityPropType = PropTypes.shape({
  amount: PropTypes.number,
  classDate: PropTypes.string,
  classDisabled: PropTypes.bool,
  classDisabledDueToPricingOptions: PropTypes.bool,
  classEntityType: PropTypes.string,
  className: PropTypes.string,
  classtivityClassId: PropTypes.number,
  currency: PropTypes.string,
  disabled: PropTypes.bool,
  disabledLevel: PropTypes.string,
  disabledReason: PropTypes.string,
  disabledSchedule: PropTypes.bool,
  endTime: PropTypes.string,
  futureScheduleCount: PropTypes.number,
  id: PropTypes.number,
  isCancelled: PropTypes.bool,
  isFull: PropTypes.bool,
  networkSpotsAllocated: PropTypes.number,
  networkSpotsTaken: PropTypes.number,
  opportunitySource: PropTypes.string,
  premiumSpotsTaken: PropTypes.number,
  previouslyActedOn: PropTypes.bool,
  recommendation: PropTypes.number,
  reimbursementRate: PropTypes.number,
  scheduleDisabled: PropTypes.bool,
  scheduleId: PropTypes.number,
  spotsAdded: PropTypes.number,
  startDateTime: PropTypes.instanceOf(Date),
  startTime: PropTypes.string,
  takenSpots: PropTypes.number,
  teacherId: PropTypes.number,
  teacherName: PropTypes.string,
  tooltip: PropTypes.string,
  totalCapacity: PropTypes.number,
  totalClasspassCapacity: PropTypes.number,
  totalClasspassPremiumReservations: PropTypes.number,
  totalClasspassReservations: PropTypes.number,
  totalNetworkReservations: PropTypes.number,
  totalReservations: PropTypes.number,
  venueId: PropTypes.number,
});

export const schedulePropTypes = {
  apiGuid: PropTypes.number,
  apiNoCapacity: PropTypes.bool,
  classpassAlmostSoldOut: PropTypes.bool,
  classpassSoldOut: PropTypes.bool,
  classpassSpotsUnfilled: PropTypes.number,
  duration: PropTypes.number,
  endDateTime: PropTypes.instanceOf(Date),
  endTime: PropTypes.string,
  entityType: PropTypes.string,
  hasIntegrationData: PropTypes.bool,
  isCancellable: PropTypes.bool,
  isCancelled: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isFull: PropTypes.bool,
  isInProgress: PropTypes.bool,
  isInThePast: PropTypes.bool,
  isLocked: PropTypes.bool,
  isManualStudio: PropTypes.bool,
  isWellness: PropTypes.bool,
  opportunity: opportunityPropType,
  reservationsStopped: PropTypes.bool,
  resource: PropTypes.shape({
    resourceId: PropTypes.number,
    resourceName: PropTypes.string,
  }),
  scheduleClass: PropTypes.shape({
    classId: PropTypes.number,
    classDisabled: PropTypes.bool,
    className: PropTypes.string,
    classPublished: PropTypes.bool,
    classSource: PropTypes.string,
  }),
  scheduleDisabled: PropTypes.bool,
  scheduleId: PropTypes.number,
  scheduleName: PropTypes.string,
  schedulePublished: PropTypes.bool,
  smartspotEnabled: PropTypes.bool,
  smartspotEnabledForVenue: PropTypes.bool,
  smartspotMode: PropTypes.string,
  smartspotNetworkSpotCap: PropTypes.number,
  smartspotReason: PropTypes.string,
  source: PropTypes.string,
  spots: PropTypes.shape({
    booked: PropTypes.number,
    classpassTotal: PropTypes.number,
    classpassBooked: PropTypes.number,
    classpassDemand: PropTypes.number,
    deferredReservations: PropTypes.number,
    unfulfilledDeferredReservations: PropTypes.number,
    maxCapacity: PropTypes.number,
    takenSpots: PropTypes.number,
    total: PropTypes.number,
  }),
  startDate: PropTypes.string,
  startDateTime: PropTypes.instanceOf(Date),
  startTime: PropTypes.string,
  venueId: PropTypes.number,
  venueTimezone: PropTypes.string,
  listPresentation: PropTypes.shape({
    addSpot: PropTypes.shape({
      spotsToAdd: PropTypes.number,
      type: PropTypes.string,
      visible: PropTypes.bool,
    }),
    badges: PropTypes.shape({
      newClientsCount: PropTypes.number,
      smartspotOffForSchedule: PropTypes.bool,
      waitingCount: PropTypes.number,
    }),
    fillBar: PropTypes.shape({
      capacityTotal: PropTypes.number,
      classpassAllocated: PropTypes.number,
      classpassBooked: PropTypes.number,
      directBooked: PropTypes.number,
      unfilled: PropTypes.number,
    }),
  }),
};

export const scheduleListPropTypes = PropTypes.arrayOf(PropTypes.shape(schedulePropTypes));

export const scheduleInsightsPropTypes = {
  scheduleId: PropTypes.number,
  history: scheduleListPropTypes,
  payouts: PropTypes.shape({
    totalRevenue: PropTypes.number,
  }),
};

export const navConfigPropTypes = PropTypes.shape({
  Icon: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
  isExternal: PropTypes.bool,
  message: intlMessage,
  path: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
});
navConfigPropTypes.children = PropTypes.arrayOf(navConfigPropTypes);

export const manualInstructorReviewsPropTypes = PropTypes.arrayOf(
  PropTypes.shape({
    rating: PropTypes.number,
    date: PropTypes.string,
    className: PropTypes.string,
    comment: PropTypes.string,
  })
);

export const manualUserVenuesPropTypes = PropTypes.arrayOf(
  PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  })
);

export const manualSchedulePropTypes = PropTypes.shape({
  id: PropTypes.number,
  status: PropTypes.string,
  recurring: PropTypes.bool,
  startDate: PropTypes.string,
  peakTime: PropTypes.bool,
  durationInMinutes: PropTypes.number,
  recurrence: PropTypes.shape({
    days: PropTypes.arrayOf(PropTypes.string),
  }),
  recurrenceEndDate: PropTypes.string,
  capacity: PropTypes.number,
  networkSpots: PropTypes.number,
  classId: PropTypes.number,
  classTitle: PropTypes.string,
  instructorId: PropTypes.number,
  instructorName: PropTypes.string,
  roomId: PropTypes.number,
  roomName: PropTypes.string,
  venueId: PropTypes.number,
  venueName: PropTypes.string,
});

export const manualSchedulesPropTypes = PropTypes.arrayOf(manualSchedulePropTypes);

export const manualSchedulesInstancesPropTypes = PropTypes.objectOf(
  PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      isFuture: PropTypes.bool,
      schedule: manualSchedulePropTypes,
      startDate: PropTypes.string,
      startDatetime: PropTypes.string,
      status: PropTypes.string,
    })
  )
);

export const schedulesInstructorsPropTypes = PropTypes.arrayOf(
  PropTypes.shape({
    id: PropTypes.number,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
  })
);

export const schedulesClassesPropTypes = PropTypes.arrayOf(
  PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  })
);

export const manualSchedulesSummaryPreviewPropTypes = {
  selectedSchedule: PropTypes.shape({
    startDate: PropTypes.string,
    durationInMinutes: PropTypes.number,
    instructorName: PropTypes.string,
    networkSpots: PropTypes.number,
    recurring: PropTypes.bool,
    recurrence: PropTypes.shape({
      days: PropTypes.arrayOf(PropTypes.string),
    }),
  }),
};

export const bulletItemsPropTypes = PropTypes.arrayOf(intlMessage);

export const guideColumnPropTypes = PropTypes.shape({
  sectionTitle: intlMessage,
  columnTitle: intlMessage,
  bulletItems: bulletItemsPropTypes,
});

export const guideDataPropTypes = PropTypes.shape({
  leftColumn: guideColumnPropTypes,
  rightColumn: guideColumnPropTypes,
});

const tagShape = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  name: PropTypes.oneOfType([PropTypes.string, intlMessage]),
};
tagShape.subtags = PropTypes.arrayOf(PropTypes.shape(tagShape));
export const tagPropTypes = PropTypes.shape(tagShape);
export const tagsPropTypes = PropTypes.arrayOf(tagPropTypes);

export const UIShape = {
  viewportWidth: PropTypes.number,
  xs: PropTypes.bool,
  sm: PropTypes.bool,
  md: PropTypes.bool,
  lg: PropTypes.bool,
  xl: PropTypes.bool,
};

export const reviewPropTypes = {
  ratingId: PropTypes.number,
  className: PropTypes.string,
  createdOn: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  review: PropTypes.string,
  teacherFullName: PropTypes.string,
  rating: PropTypes.string,
  name: PropTypes.string,
  profilePictureUrl: PropTypes.string,
  isPublic: PropTypes.bool,
};

export const autocompleteItemPropTypes = PropTypes.shape({
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  type: PropTypes.string,
});

export const summaryGridItemCompShape = {
  message: intlMessage,
  value: PropTypes.number,
  currencyCode: PropTypes.string,
  format: PropTypes.string,
  tooltip: PropTypes.element,
  id: PropTypes.string,
};

const summaryGridItemShape = {
  ...summaryGridItemCompShape,
  key: PropTypes.string,
};

const summaryGridItemProptypes = PropTypes.shape(summaryGridItemShape);

export const summaryGridItemsProptypes = PropTypes.arrayOf(summaryGridItemProptypes);

export const dashboardUsersVenuesPropTypes = PropTypes.arrayOf(
  PropTypes.shape({ venueId: PropTypes.number, venueName: PropTypes.string })
);

export const dashboardUserPropTypes = {
  userId: PropTypes.number,
  email: PropTypes.string,
  venues: PropTypes.arrayOf(PropTypes.shape({ venueId: PropTypes.number, venueName: PropTypes.string })),
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  role: PropTypes.string,
  active: PropTypes.bool,
};

export const dashboardUsersPropTypes = PropTypes.arrayOf(PropTypes.shape(dashboardUserPropTypes));

export const servicesPropTypes = PropTypes.arrayOf(
  PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    description: PropTypes.string,
    duration: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    practitioners: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        fullName: PropTypes.string,
      })
    ),
    resources: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      })
    ),
  })
);

const reportsVenuesCardDataItemPropType = PropTypes.shape({
  // there are also venueId keys with number values
  endDate: PropTypes.instanceOf(Date),
  startDate: PropTypes.instanceOf(Date),
  total: PropTypes.number,
  allRemaining: PropTypes.number,
});

export const reportsVenuesCardDataPropType = PropTypes.arrayOf(reportsVenuesCardDataItemPropType);

const reportsVenuesCardLegendItemPropType = PropTypes.shape({
  count: PropTypes.number,
  total: PropTypes.number,
  venueId: PropTypes.number,
  venueName: PropTypes.string,
  venueSubtitle: PropTypes.string,
});

export const reportsVenuesCardLegendPropType = PropTypes.arrayOf(reportsVenuesCardLegendItemPropType);

export const reportsVenuesCardInfoPropType = PropTypes.shape({
  data: reportsVenuesCardDataPropType,
  isRevenueCard: PropTypes.bool,
  legend: reportsVenuesCardLegendPropType,
  type: PropTypes.string,
  venueIds: PropTypes.arrayOf(PropTypes.string),
});

export const reportsVenuesCardsInfoPropType = PropTypes.arrayOf(reportsVenuesCardInfoPropType);

export const feedbackPropTypes = PropTypes.shape({
  reportedFeedback: PropTypes.arrayOf(PropTypes.oneOf(Object.values(VenueUserFeedbackOptionType))),
  description: PropTypes.string,
  trialAbuseReported: PropTypes.bool,
  disruptiveUserReported: PropTypes.bool,
  allowedOptions: PropTypes.arrayOf(PropTypes.oneOf(Object.values(VenueUserFeedbackOptionType))),
});

export const reservationPropTypes = {
  attendanceStatus: PropTypes.bool,
  cancellationType: PropTypes.oneOf(Object.values(ReservationCancellationType)),
  classId: PropTypes.number,
  className: PropTypes.string,
  createdOn: PropTypes.instanceOf(Date),
  date: PropTypes.string,
  dateCreated: PropTypes.string,
  email: PropTypes.string,
  endDatetime: PropTypes.string,
  endTime: PropTypes.string,
  feedback: feedbackPropTypes,
  isCancellable: PropTypes.bool,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  lastUpdated: PropTypes.instanceOf(Date),
  profileUrl: PropTypes.string,
  reservationId: PropTypes.number,
  reservationStatus: PropTypes.oneOf(Object.values(ReservationStatus)),
  scheduleId: PropTypes.number,
  startDatetime: PropTypes.string,
  startTime: PropTypes.string,
  teacherId: PropTypes.number,
  teacherName: PropTypes.string,
  teacherOpenapiId: PropTypes.number,
  userId: PropTypes.number,
  venueId: PropTypes.number,
  venueName: PropTypes.string,
  visitNumberToVenue: PropTypes.number,
  waiverVersionIdSigned: PropTypes.number,
  waiverSignedTimestamp: PropTypes.string,
};

export const rosterPropTypes = PropTypes.arrayOf(PropTypes.shape(reservationPropTypes));

export const reservationForAppointmentsPropTypes = {
  ...reservationPropTypes,
  startDateTime: PropTypes.instanceOf(Date),
  endDateTime: PropTypes.instanceOf(Date),
  isReadOnly: PropTypes.bool,
  duration: PropTypes.number,
};

export const rosterForAppointmentsPropTypes = PropTypes.arrayOf(PropTypes.shape(reservationForAppointmentsPropTypes));

export const moneyPropType = PropTypes.shape({
  amount: PropTypes.number,
  currency: PropTypes.string,
});

const manageServicesAvailabilityDay = PropTypes.arrayOf(
  PropTypes.shape({
    startTime: PropTypes.string,
    endTime: PropTypes.string,
  })
);

export const manageServicesAvailabilityShape = {
  recurrence: PropTypes.shape({
    Mo: manageServicesAvailabilityDay,
    Tu: manageServicesAvailabilityDay,
    We: manageServicesAvailabilityDay,
    Th: manageServicesAvailabilityDay,
    Fr: manageServicesAvailabilityDay,
    Sa: manageServicesAvailabilityDay,
    Su: manageServicesAvailabilityDay,
  }),
  singleDayExceptions: PropTypes.arrayOf(
    PropTypes.shape({
      date: PropTypes.string,
      startTime: PropTypes.string,
      endTime: PropTypes.string,
    })
  ),
};

export const blockOffTimePropType = PropTypes.shape({
  id: PropTypes.number,
  itemId: PropTypes.number,
  itemName: PropTypes.string,
  itemType: PropTypes.string,
  startDatetime: PropTypes.instanceOf(Date),
  endDatetime: PropTypes.instanceOf(Date),
});

const convertedPricePropType = PropTypes.shape({
  amount: PropTypes.string,
  currencyCode: PropTypes.string,
  decimalAmount: PropTypes.number,
});

export const packPropType = {
  addonId: PropTypes.number,
  addonType: PropTypes.string,
  addonVersionId: PropTypes.number,
  currencyCode: PropTypes.string,
  customerName: PropTypes.string,
  deleted: PropTypes.bool,
  editable: PropTypes.bool,
  expirationDays: PropTypes.number,
  partnerName: PropTypes.string,
  price: PropTypes.number,
  quantityPack: PropTypes.number,
  retailPrice: PropTypes.number,
  venueEligibility: PropTypes.string,
  convertedPrice: convertedPricePropType,
  convertedRetailPrice: convertedPricePropType,
};

export const packPayloadPropType = PropTypes.shape({
  addonId: PropTypes.number,
  packQuantity: PropTypes.number,
  expirationDays: PropTypes.number,
  venueIds: PropTypes.arrayOf(PropTypes.string),
  retailPrice: PropTypes.number,
});

export const packOptionsPropType = PropTypes.shape({
  expirationPeriods: PropTypes.arrayOf(PropTypes.number),
  quantityPacks: PropTypes.arrayOf(PropTypes.number),
  venueIds: PropTypes.arrayOf(PropTypes.number),
});

export const msiLastChangePropTypes = PropTypes.shape({
  timestampSeconds: PropTypes.number,
  userSource: PropTypes.string,
  userEmail: PropTypes.string,
});

export const dayShape = PropTypes.shape({
  en: PropTypes.string,
  long: PropTypes.string,
  short: PropTypes.string,
  narrow: PropTypes.string,
});

export const templateCopyDataPropType = PropTypes.shape({
  currency: PropTypes.string,
  title: PropTypes.string,
  terms: PropTypes.string,
  effectiveDateLabel: PropTypes.string,
  fieldLabels: PropTypes.shape({
    signatoryName: PropTypes.string,
    signatoryTitle: PropTypes.string,
    legalEntity: PropTypes.string,
    partnerBrandName: PropTypes.string,
  }),
  otherTermsText: PropTypes.string,
  payoutDescription: PropTypes.string,
  payoutTableHeaders: PropTypes.shape({
    venue: PropTypes.string,
    minRate: PropTypes.string,
  }),
  payoutTableDisclaimer: PropTypes.string,
  acknowledgeHeader: PropTypes.string,
  acknowledgeText: PropTypes.string,
  acknowledgeButton: PropTypes.string,
});

export const payoutTableRowPropType = PropTypes.shape({
  offeringId: PropTypes.number,
  offeringName: PropTypes.string,
  floor: PropTypes.number,
  ceiling: PropTypes.number,
  average: PropTypes.number,
  rateText: PropTypes.string,
});

export const insightsDataColumnPropType = PropTypes.arrayOf(
  PropTypes.arrayOf(
    PropTypes.shape({
      category: PropTypes.string,
      defaultRange: PropTypes.string,
      isObfuscated: PropTypes.bool,
      ranges: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string,
          value: PropTypes.string,
        })
      ),
      title: PropTypes.string,
      url: PropTypes.string,
    })
  )
);

export const reservationTransactionsPropType = PropTypes.arrayOf(
  PropTypes.shape({
    effectiveDate: PropTypes.string,
    reservationId: PropTypes.number,
    className: PropTypes.string,
    dateTime: PropTypes.instanceOf(Date),
    userName: PropTypes.string,
    status: PropTypes.string,
    amount: PropTypes.number,
    currencyCode: PropTypes.string,
  })
);

export const payoutPropType = PropTypes.shape({
  payoutId: PropTypes.number,
  payoutAsOfDate: PropTypes.string,
  previousPayoutAsOfDate: PropTypes.string,
  payoutSubmissionDate: PropTypes.string,
  status: PropTypes.oneOf([PAYOUT_STATUS.FAILED, PAYOUT_STATUS.POSTED]),
  currencyCode: PropTypes.string,
  revenueFromReservations: PropTypes.number,
  revenueFromReservationsConverted: PropTypes.number,
  adjustments: PropTypes.number,
  adjustmentConverted: PropTypes.number,
  contractual: PropTypes.number,
  contractualConverted: PropTypes.number,
  totalVenuePayout: PropTypes.number,
  totalVenuePayoutConverted: PropTypes.number,
});
